import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";

const MEMBERS = ["Hongjoong", "Seonghwa", "Yunho", "Yeosang", "San", "Mingi", "Wooyoung", "Jongho"];
const APPS = ["KakaoTalk", "Line", "HelloLive"];
const EMPTY = { first_name: "", middle_name: "", last_name: "", dob: "", email: "", phone: "", nationality: "", member: "", apps: [],
  hellolive_username: "", hellolive_password: "", kakaotalk_username: "", line_username: "" };
const input = "mt-1 w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-white placeholder:text-zinc-600";

const Text = ({ f, set, k, label, ...rest }) => (
  <label className="block text-xs text-zinc-300">
    {label}
    <input data-testid={`fancall-info-${k.replace(/_/g, "-")}`} value={f[k]} onChange={(e) => set({ ...f, [k]: e.target.value })} className={input} {...rest} />
  </label>
);

const AppFields = ({ f, set }) => (
  <>
    {f.apps.includes("HelloLive") && (
      <div className="grid sm:grid-cols-2 gap-3 rounded-xl border border-white/10 p-3">
        <div className="sm:col-span-2 text-[11px] uppercase tracking-widest text-zinc-500">HelloLive account</div>
        <Text f={f} set={set} k="hellolive_username" label="Username" />
        <Text f={f} set={set} k="hellolive_password" label="Password" type="password" autoComplete="off" />
      </div>
    )}
    {f.apps.includes("KakaoTalk") && <Text f={f} set={set} k="kakaotalk_username" label="Your KakaoTalk username" />}
    {f.apps.includes("Line") && <Text f={f} set={set} k="line_username" label="Your Line username" />}
  </>
);

export const FancallInfoDialog = ({ raffle, joinerId, onClose, onDone }) => {
  const [f, set] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const toggleApp = (a) => set({ ...f, apps: f.apps.includes(a) ? f.apps.filter((x) => x !== a) : [...f.apps, a] });
  const submit = async () => {
    if (!f.member) return toast.error("Pick the member you'd like to apply for");
    if (!f.apps.length) return toast.error("Pick at least one call app");
    if (!/^\d{6}$/.test(f.dob)) return toast.error("Date of birth must be 6 digits: YYMMDD");
    setBusy(true);
    try {
      await api.post(`/raffles/${raffle.raffle_id}/fancall-info/${joinerId}`, f);
      toast.success("Fancall Information sent");
      set(EMPTY);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!raffle} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="fancall-info-dialog" className="bg-zinc-950 border-zinc-800 text-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fancall Information</DialogTitle>
          <DialogDescription className="text-zinc-400">{raffle?.name}. Only the admin team can see this. You can send it once; ask an admin if you need to change it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid sm:grid-cols-3 gap-3">
            <Text f={f} set={set} k="first_name" label="First Name" />
            <Text f={f} set={set} k="middle_name" label="Middle Name" />
            <Text f={f} set={set} k="last_name" label="Last Name" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Text f={f} set={set} k="dob" label="Date of Birth (YYMMDD)" inputMode="numeric" maxLength={6} placeholder="990131" />
            <Text f={f} set={set} k="nationality" label="Nationality" />
            <Text f={f} set={set} k="email" label="Email" type="email" />
            <Text f={f} set={set} k="phone" label="Phone Number" type="tel" />
          </div>
          <label className="block text-xs text-zinc-300">
            Which member would you like to apply for?
            <select data-testid="fancall-info-member" value={f.member} onChange={(e) => set({ ...f, member: e.target.value })} className={input}>
              <option value="">Choose a member</option>
              {MEMBERS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </label>
          <div className="text-xs text-zinc-300">Select your Preferred Call App
            <div className="mt-1.5 flex flex-wrap gap-4">
              {APPS.map((a) => (
                <label key={a} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox data-testid={`fancall-info-app-${a.toLowerCase()}`} checked={f.apps.includes(a)} onCheckedChange={() => toggleApp(a)} />{a}
                </label>
              ))}
            </div>
          </div>
          <AppFields f={f} set={set} />
        </div>
        <DialogFooter>
          <Button variant="ghost" data-testid="fancall-info-cancel" onClick={onClose}>Cancel</Button>
          <Button data-testid="fancall-info-submit" disabled={busy} onClick={submit}>{busy ? "Sending…" : "Send"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
